// static class
import deepmerge from 'deepmerge';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import Cookies from 'js-cookie';

class Utils {
  static isServerSide(config = {}) {
    if (config && config.isServer) {
      return true;
    }
    return typeof window === 'undefined';
  }

  static generateUUID() {
    return uuidv4();
  }

  static getCookie(cookieName) {
    return Cookies.get(cookieName);
  }

  static setCookie(name, value, attributes = {}) {
    return Cookies.set(name, value, attributes);
  }

  static removeCookie(name, attributes = {}) {
    return Cookies.remove(name, attributes);
  }

  static getRecaptchaActionName(url, publicationName) {
    url = url.replace('/auth', '').replace('/api/', '');
    const action = url.split('/').join('_').toUpperCase();
    return `${publicationName.toUpperCase()}_${action}`;
  }

  static parseToJSON(data) {
    try {
      if (typeof data === 'string') {
        return JSON.parse(data);
      }
    } catch (e) {

    }
    return data;
  }

  static deepMergeWithArrayOverwrite(obj1, obj2) {
    const overwriteMerge = (destinationArray, sourceArray) => sourceArray;
    return deepmerge(obj1, obj2, { arrayMerge: overwriteMerge });
  }

  static deepMergeWithArrayCombineUniqueElements(obj1, obj2) {
    const combine = (destinationArray, sourceArray) => [...new Set([...destinationArray, ...sourceArray])];
    return deepmerge(obj1, obj2, { arrayMerge: combine });
  }

  static createError(e, data) {
    let err = new Error(`${JSON.stringify(e)}. Data: ${JSON.stringify(data)}`);
    err = {
      ...data,
      ...err,
    };
    return err;
  }

  static storeInStorage(key, value, expiration = 0, expirationType = 'millisecond', storage) {
    if (expirationType.endsWith('s')) {
      // old moment() support, the units were in plural
      expirationType = expirationType.substring(0, expirationType.length - 1);
    }
    const expire = dayjs().add(expiration, expirationType);
    const data = {
      value,
      expire_at: expire.toISOString()
    };
    storage.setItem(key, JSON.stringify(data));
  }

  static readFromStorage(key, storage) {
    const stringData = storage.getItem(key);
    console.log(`[Storage Read] ${key} -> ${stringData}`);
    if (!stringData) {
      return null;
    }
    let data = {};
    try {
      data = JSON.parse(stringData);
      if (!data.expire_at || dayjs(data.expire_at) < dayjs()) {
        console.log(`[Storage Expired] ${key} -> ${stringData}`);
        return null;
      }
      console.log(`[Storage Data] ${key} -> ${stringData}`);
      return data.value;
    } catch (e) {
      return null;
    }
  }

  static deleteFromStorage(key, storage) {
    storage.removeItem(key);
  }

  static readAndDeleteFromStorage(key, storage) {
    const value = Utils.readFromStorage(key, storage);
    Utils.deleteFromStorage(key, storage);
    return value;
  }

  static storeInLocalStorage(key, value, expiration = 0, expirationType = 'millisecond') {
    return Utils.storeInStorage(key, value, expiration, expirationType, window.localStorage);
  }

  static readFromLocalStorage(key) {
    return Utils.readFromStorage(key, window.localStorage);
  }

  static deleteFromLocalStorage(key) {
    return Utils.deleteFromStorage(key, window.localStorage);
  }

  static readAndDeleteFromLocalStorage(key) {
    return Utils.readAndDeleteFromStorage(key, window.localStorage);
  }

  static storeInSessionStorage(key, value, expiration = 0, expirationType = 'millisecond') {
    return Utils.storeInStorage(key, value, expiration, expirationType, window.sessionStorage);
  }

  static readFromSessionStorage(key) {
    return Utils.readFromStorage(key, window.sessionStorage);
  }

  static deleteFromSessionStorage(key) {
    return Utils.deleteFromStorage(key, window.sessionStorage);
  }

  static readAndDeleteFromSessionStorage(key) {
    return Utils.readAndDeleteFromStorage(key, window.sessionStorage);
  }

  static btoa(val) {
    return Utils.isServerSide() ? Buffer.from(val).toString('base64') : btoa(val);
  }

  static getBlaizeSession() {
    return Utils.isServerSide() ? '' : Utils.getCookie('blaize_session');
  };

  static getOneIdInfo() {
    return Utils.isServerSide() ? '' : Utils.getCookie('oneid_info');
  };

  static getDetailOneIdInfo() {
    const defaultValue = {
      version: '',
      authenticated: false,
      username: '',
      oneid: '',
      session: Utils.getBlaizeSession(),
      sessionFromOneIdInfoCookie: ''

    };
    let oneidInfo = Utils.getOneIdInfo();
    if (!oneidInfo) return defaultValue;
    oneidInfo = atob(oneidInfo).split('|');
    defaultValue.authenticated = true;
    try {
      defaultValue.version = oneidInfo[0];
      defaultValue.sessionFromOneIdInfoCookie = oneidInfo[1];
      defaultValue.oneid = oneidInfo[2];
      defaultValue.username = oneidInfo[3];
    } catch (e) {
      console.error('[Error decoding oneid info]', e);
    }
    return defaultValue;
  };

  static async delayForZephrLogin() {
    // Delayed to allow Zephr session propagate properly
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  static isAppDevice() {
    if (Utils.isServerSide()) {
      // Todo: fix it later
      return false;
    }
    return ['view=', 'type=m'].filter(val => window.location.search.includes(val)).length > 0;
  };

  static getUserAgent() {
    let userAgent = '';
    try {
      if (Utils.isAppDevice()) {
        userAgent += 'app device: ';
      }
      userAgent += `${navigator.userAgent}`;
    } catch (e) {
      console.error('Could not determine user agent', e);
      userAgent += ``;
    }
    return userAgent;
  };

  static shouldSendLogToServer(config, isError) {
    if (Utils.isServerSide()) {
      return false;
    }
    const extraInfo = config.extraInfo || {};
    if (isError || extraInfo.forceSendLog) {
      return true;
    }
    // If not error case
    // These are set on nhst-sub-zephr-feature project
    if (
      window?._nhstFeatures?.config?.sendLogToServerLogLevel === 'error' ||
      window?._nhstFeatures?.config?.stopSendingLogsToServer === true ||
      extraInfo.sendSuccessLog === false
    ) {
      return false;
    }
    return true;
  }

  static isIOS() {
    const userAgent = Utils.getUserAgent();
    return /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  };

  static isAndroid() {
    const userAgent = Utils.getUserAgent();
    return /Android/i.test(userAgent);
  };

  static isIOSApp() {
    return Utils.isIOS() && Utils.isAppDevice();
  };

  static isAndroidApp() {
    return Utils.isAndroid() && Utils.isAppDevice();
  };
}

export default Utils;
